/**
 * The record's own company, rendered in the app bar's company slot.
 *
 * The counterpart of `CompanyScopeSwitcher`: on a collection surface that slot
 * filters, and here — on a single-record surface — it states which legal entity
 * the record in front of you belongs to. Same slot, same chrome, so the reader
 * never has to work out which mode it is in to read the answer.
 *
 * Read-only by default. When the page says the record is still a draft AND
 * supplies a writer, it becomes a picker: an S document locks its company at
 * posting (docs/platform/multi-company-design.md § "On-record company"). The
 * page owns the write and its side effects — changing a draft's company
 * re-issues any per-company document number and re-resolves FX — this component
 * only surfaces the control.
 */
import { useTranslation } from 'react-i18next';

import { cn } from '../../../lib/utils';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import { Skeleton } from '../../../components/ui/skeleton';
import { useMyCompanies } from '@/shared/users/hooks/useCompanies';
import { usePageCompany } from '../../contexts/PageCompanyContext';
import { useCompanyScopingMapStore, isModuleCompanyScoped } from '../../utils/companyScopingStore';
import { BidiText } from '../common/BidiText';
import { CompanyIdentity, COMPANY_CONTROL_TRIGGER_CLASS } from './CompanyIdentity';

export interface PageCompanyIndicatorProps {
  className?: string;
  /**
   * Distinguishes the two mount points. The app-bar arm and the mobile in-page
   * arm are CSS complements (`hidden sm:flex` / `sm:hidden`), so BOTH are in the
   * DOM at once and only one is ever visible — a shared test id makes every
   * `getByTestId` a strict-mode violation.
   */
  testId?: string;
}

export function PageCompanyIndicator({
  className,
  testId = 'page-company-indicator',
}: PageCompanyIndicatorProps) {
  const { t } = useTranslation(['common', 'user_management']);
  const { data, isLoading: companiesLoading } = useMyCompanies();
  const pageCompany = usePageCompany();
  // Re-evaluate when a tenant admin toggles this module's scoping (WI-4).
  useCompanyScopingMapStore();

  const companies = data?.data ?? [];

  if (!pageCompany) return null;
  // Hide-when-single, and hide for a module that does not carry the dimension —
  // the same two gates the scope switcher and DocumentCompanyField apply.
  if (companies.length <= 1) return null;
  if (!isModuleCompanyScoped(pageCompany.moduleId)) return null;

  if (pageCompany.isLoading || companiesLoading) {
    // Hold the slot. Publishing late and letting the control pop in reflows the
    // whole header cluster when the record lands.
    return <Skeleton className={cn('h-9 w-40', className)} data-testid={`${testId}-loading`} />;
  }

  // `undefined` (not found) and `null` (genuinely unset) are different facts but
  // render alike: in both cases we cannot name a company. A record reached by
  // deep link outside the user's accessible set lands here.
  const company = pageCompany.companyId
    ? companies.find((c) => c.id === pageCompany.companyId)
    : null;

  if (pageCompany.editable && pageCompany.onChange) {
    // Only companies a new document could legitimately be filed under: an
    // inactive entity, and the virtual elimination entity that exists solely for
    // consolidated reporting, are not places to move a live document to.
    const assignable = companies.filter((c) => c.status === 'active' && !c.is_elimination);
    return (
      <Select
        value={pageCompany.companyId ?? undefined}
        onValueChange={(value) => pageCompany.onChange?.(value)}
      >
        <SelectTrigger
          data-testid={testId}
          className={cn(COMPANY_CONTROL_TRIGGER_CLASS, 'hover:bg-accent focus:ring-1 [&>span]:min-w-0', className)}
          aria-label={t('common:multiCompany.recordCompanyAria')}
        >
          <CompanyIdentity company={company} iconOnly />
          <SelectValue placeholder={t('common:multiCompany.unassignedCompany')}>
            {/* Company names are user data and mix scripts, so the name gets its
                own bidi isolate — the read-only arm below gets this via
                CompanyIdentity, and the two arms must not disagree. The
                translated fallback is our own copy and deliberately does not. */}
            {company ? (
              <BidiText>{company.name}</BidiText>
            ) : (
              t('common:multiCompany.unassignedCompany')
            )}
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          {assignable.map((c) => (
            <SelectItem key={c.id} value={c.id}>
              <CompanyIdentity company={c} size="sm" />
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }

  return (
    <span
      data-testid={testId}
      className={cn(
        COMPANY_CONTROL_TRIGGER_CLASS,
        'inline-flex items-center rounded-md border px-3',
        className,
      )}
      // Not a control: it states a fact about the record. Announce it as one.
      role="status"
      aria-label={t('common:multiCompany.recordCompanyAria')}
    >
      <CompanyIdentity company={company} />
    </span>
  );
}
